import { parseAdminError } from './adminUi.js'
import { extractArrayEnvelope } from './contracts.js'

const KNOWN_LOTTERY_STATUSES = new Set(['draft', 'active', 'paused', 'closed', 'drawing', 'drawn', 'archived'])

export const normalizeLotteryStatus = (lottery) => {
  const status = String(lottery?.status ?? '').trim().toLowerCase()
  if (KNOWN_LOTTERY_STATUSES.has(status)) return status

  // Backward compatibility with older payloads that only expose lock flag.
  if (lottery?.is_locked === true) return 'closed'
  return 'draft'
}

export const getLotteryLifecycleMeta = (lotteryOrStatus) => {
  const status = typeof lotteryOrStatus === 'string'
    ? normalizeLotteryStatus({ status: lotteryOrStatus })
    : normalizeLotteryStatus(lotteryOrStatus)

  const map = {
    draft: { label: 'Draft', className: 'warning' },
    active: { label: 'Active', className: 'active' },
    paused: { label: 'Paused', className: 'warning' },
    closed: { label: 'Closed', className: 'neutral' },
    drawing: { label: 'Drawing', className: 'warning' },
    drawn: { label: 'Drawn', className: 'neutral' },
    archived: { label: 'Archived', className: 'deleted' },
  }

  return map[status] || { label: 'Draft', className: 'warning' }
}

export const canEditLottery = (lottery) => {
  const status = normalizeLotteryStatus(lottery)
  return status === 'draft' || status === 'active'
}

export const canPauseLottery = (lottery) => normalizeLotteryStatus(lottery) === 'active'
export const canResumeLottery = (lottery) => normalizeLotteryStatus(lottery) === 'paused'
export const canCloseLottery = (lottery) => normalizeLotteryStatus(lottery) === 'active'
export const canArchiveLottery = (lottery) => {
  const status = normalizeLotteryStatus(lottery)
  return status === 'closed' || status === 'drawn'
}

export const canDrawLottery = (lottery) => {
  const status = normalizeLotteryStatus(lottery)
  return status === 'active' || status === 'closed'
}

export const isLotteryReadOnly = (lottery) => {
  const status = normalizeLotteryStatus(lottery)
  return status === 'archived' || status === 'drawn'
}

export const toLotteryAuditMessage = (event, lotteryName = 'Lottery') => {
  const map = {
    LOTTERY_CREATED: `${lotteryName} created successfully.`,
    LOTTERY_UPDATED: `${lotteryName} updated successfully.`,
    LOTTERY_PAUSED: `${lotteryName} paused successfully.`,
    LOTTERY_RESUMED: `${lotteryName} resumed successfully.`,
    LOTTERY_CLOSED: `${lotteryName} closed successfully.`,
    LOTTERY_ARCHIVED: `${lotteryName} archived successfully.`,
    LOTTERY_DRAW_EXECUTED: `${lotteryName} draw executed successfully.`,
  }

  return map[event] || 'Operation completed successfully.'
}

const toNumericOrNull = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export const normalizeDrawHistoryEntry = (entry, index = 0) => {
  const payload = entry && typeof entry === 'object' ? entry : {}
  const winners = Array.isArray(payload.winners)
    ? payload.winners
    : payload.winner
      ? [payload.winner]
      : payload.user_id || payload.winning_number
        ? [payload]
        : []

  // Strict-scope: render only backend-provided fields without inference chains.
  // Removed fields that lack explicit backend contract evidence:
  // - executedBy (used inferred fallback chains; not consistently provided by backend)
  // - rerunBlocked (unclear backend semantics; no contract evidence)
  // - archivedResultReference (fallback chain from audit_id; not verified)
  // Guard layer v1: avoid synthetic execution fallbacks for timestamp/status/count.
  return {
    id: payload.id ?? payload.execution_id ?? payload.result_id ?? `execution-${index}`,
    executedAt: payload.executed_at ?? null,
    winnerCount: toNumericOrNull(payload.winner_count),
    executionStatus: String(payload.execution_status ?? '').trim().toLowerCase(),
    executionNotes: payload.execution_notes ?? '',
    integrity: {
      totalTickets: toNumericOrNull(payload.total_tickets_included ?? payload.tickets_count ?? payload.metadata?.tickets_count),
      eligibleParticipants: toNumericOrNull(payload.eligible_participants_count ?? payload.participants_count ?? payload.metadata?.participants_count),
      excludedOrRefunded: toNumericOrNull(payload.excluded_or_refunded_count ?? payload.refunded_count ?? payload.metadata?.excluded_count),
      randomizationMetadata: payload.randomization_metadata ?? payload.metadata?.randomization ?? null,
      executionMetadata: payload.execution_metadata ?? payload.metadata?.execution ?? null,
      auditReferenceIds: Array.isArray(payload.audit_reference_ids)
        ? payload.audit_reference_ids
        : payload.audit_reference_id
          ? [payload.audit_reference_id]
          : payload.metadata?.audit_reference_id
            ? [payload.metadata.audit_reference_id]
            : [],
    },
    winners,
  }
}

export const normalizeDrawHistory = (history) => {
  const rows = extractArrayEnvelope(history, { keys: ['items', 'results', 'history'] })
  return rows.map((entry, index) => normalizeDrawHistoryEntry(entry, index))
}

export const groupWinnersForDisplay = (winners) => {
  const records = Array.isArray(winners) ? winners : []
  const seenTickets = new Set()

  return records.map((winner, index) => {
    const payload = winner && typeof winner === 'object' ? winner : {}
    const ticketNumber = payload.ticket_number ?? payload.winning_number ?? null
    const userId = payload.user_id ?? payload.id ?? null
    const username = payload.username ?? payload.name ?? null
    const key = `${ticketNumber ?? 'unknown-ticket'}:${userId ?? index}`

    const duplicate = ticketNumber !== null && seenTickets.has(ticketNumber)
    if (ticketNumber !== null) seenTickets.add(ticketNumber)

    const invalid = duplicate || (!userId && !username) || (!ticketNumber && ticketNumber !== 0)

    return {
      key,
      userId,
      username,
      ticketNumber,
      record: payload,
      duplicate,
      invalid,
    }
  })
}

export const parseDrawExecutionError = (err, fallback = 'Failed to execute draw.') => {
  if (typeof err === 'string' && err.trim()) {
    const normalized = err.trim().toLowerCase()
    if (normalized.includes('draw in progress')) {
      return 'A draw execution is already in progress for this lottery. Wait for completion.'
    }
    if (normalized.includes('already drawn')) {
      return 'This lottery has already been drawn. Reload to review the recorded result.'
    }
    return err.trim()
  }

  const status = Number(err?.response?.status || 0)
  const detailRaw = err?.response?.data?.detail
  const detail = typeof detailRaw === 'string'
    ? detailRaw.toLowerCase()
    : Array.isArray(detailRaw)
      ? detailRaw.map((item) => String(item?.msg || item || '')).join(' ').toLowerCase()
      : String(err || '').toLowerCase()

  if (status === 403 || detail.includes('forbidden') || detail.includes('permission')) {
    return 'You do not have permission to execute this draw.'
  }
  if (status === 409 || detail.includes('already drawn')) {
    return 'This lottery has already been drawn. Reload to review the recorded result.'
  }
  if (detail.includes('draw in progress') || detail.includes('drawing') || detail.includes('transition active')) {
    return 'A draw execution is already in progress for this lottery. Wait for completion.'
  }
  if (detail.includes('insufficient') || detail.includes('eligible tickets')) {
    return 'Draw cannot run because there are not enough eligible tickets.'
  }
  if (detail.includes('archived')) {
    return 'Archived lotteries are read-only and cannot be drawn.'
  }
  if (status === 408 || status === 504 || detail.includes('timeout')) {
    return 'Draw execution timed out. Refresh to verify whether the result was persisted before retrying.'
  }
  if (detail.includes('partial') || detail.includes('recover')) {
    return 'A partial draw result was detected. Open execution history to validate recovery details.'
  }

  return parseAdminError(err, fallback, {
    forbidden: 'You do not have permission to execute this draw.',
    conflict: 'Draw state changed on the server. Refresh and review execution history before retrying.',
    notFound: 'This lottery is no longer available. Refresh and try again.',
  })
}



